import { utils, writeFile } from 'xlsx';
import { saveAs } from 'file-saver';
import { JsonToExcelPayload } from '../types/fileGeneration';

// tablePayload: { header: string[]; body: (string|number|null|undefined)[][] }
export const downloadExcelFromJson = (
  fileName: string,
  sheetName: string,
  tablePayload: JsonToExcelPayload
) => {
  try {
    const { headers, body } = tablePayload;
    const dataWithHeaders = [headers, ...body];
    const worksheet = utils.aoa_to_sheet(dataWithHeaders);

    // Optional: set column widths based on header length
    const colWidths = headers.map((h) => ({
      width: Math.min(Math.max(h.length + 4, 12), 40)
    }));
    // @ts-ignore - adding custom property
    worksheet['!cols'] = colWidths;

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31));
    writeFile(workbook, `${fileName}.xlsx`);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to generate Excel file', e);
  }
};

export const downloadCsvFromJson = (
  fileName: string,
  _p0: string,
  tablePayload: JsonToExcelPayload
) => {
  try {
    const { headers, body } = tablePayload;
    const escapeCell = (value: any) => {
      if (value === null || value === undefined) return '';
      const string = String(value);
      if (/[,"\n\r]/.test(string)) {
        return '"' + string.replace(/"/g, '""') + '"';
      }
      return string;
    };
    const lines: string[] = [];
    lines.push(headers.map(escapeCell).join(','));
    body.forEach((row) => {
      lines.push(row.map(escapeCell).join(','));
    });
    const csvContent = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to generate CSV file', e);
  }
};

// Wrap a plain string into lines of at most `maxChars` characters (word-aware).
const wrapText = (text: string, maxChars: number): string[] => {
  const words = (text || '').split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  words.forEach((word) => {
    if (!current) {
      current = word;
    } else if ((current + ' ' + word).length <= maxChars) {
      current += ' ' + word;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [''];
};

// Build and download a minimal single-page PDF that just prints `message`.
// Dependency-free (hand-assembled PDF with a correct xref) so bulk exports can
// emit a readable "no marks" page for a student instead of a blank/missing file.
export const downloadMessagePdf = (fileName: string, message: string) => {
  const escape = (s: string) =>
    s
      // keep it single-byte (Latin-1) so string length == byte length for the xref
      .replace(/[^\x20-\x7E]/g, '?')
      .replace(/\\/g, '\\\\')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)');

  const lines = wrapText(message, 70);
  let content = 'BT\n/F1 14 Tf\n20 TL\n72 780 Td\n';
  lines.forEach((line, index) => {
    content += index === 0 ? `(${escape(line)}) Tj\n` : `T*\n(${escape(line)}) Tj\n`;
  });
  content += 'ET';

  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>'
  ];

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((obj, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i += 1) bytes[i] = pdf.charCodeAt(i) & 0xff;

  saveAs(
    new Blob([bytes], { type: 'application/pdf' }),
    fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`
  );
};

export const downloadFileFromUrl = (
  url: string,
  fileName: string,
  fileType: string
) => {
  if (!url) {
    // Guard: without a valid URL, fetch(undefined) resolves to the current
    // page and would save the app's HTML as a bogus file. Fail loudly instead.
    console.error('downloadFileFromUrl called without a URL', { fileName });
    throw new Error('No file URL provided to download.');
  }
  fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response.blob();
    })
    .then((blob) =>
      saveAs(
        blob,
        fileName.endsWith(`.${fileType}`) ? fileName : `${fileName}.${fileType}`
      )
    )
    .catch(() => saveAs(url, `${fileName}.${fileType}`));
};
